import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChatMessage } from 'modules/chat/domain/interfaces';
import React, { useEffect, useRef } from 'react';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import ChatMessageItem from './chat-message-item';

interface Props {
  messages: ChatMessage[];
  isSending: boolean;
  onConfirmWrite?: () => Promise<void>;
  onCancelWrite?: () => Promise<void>;
}

const EXAMPLE_PROMPTS = [
  msg`Добавь команду Ястребы в турнир Первая лига`,
  msg`Создай заявку команды Ястребы на турнир`,
  msg`Покажи заявку команды Ястребы`,
];

const ChatTranscript: React.FC<Props> = ({ messages, isSending, onConfirmWrite, onCancelWrite }) => {
  const { t } = useLingui();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isSending]);

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <UDText type="body-text" color={theme.colors.Gray}>
          <Trans>Напишите ИИ-ассистенту, что нужно сделать. Например:</Trans>
        </UDText>
        <div className="flex flex-col gap-2">
          {EXAMPLE_PROMPTS.map((prompt) => {
            const text = t(prompt);
            return (
              <UDText key={prompt.id} type="caption-1" color={theme.colors.WhiteGray}>
                <Trans>«{text}»</Trans>
              </UDText>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
      {messages.map((message) => (
        <ChatMessageItem
          key={message.id}
          message={message}
          confirmBusy={isSending}
          onConfirm={message.pendingConfirmation ? onConfirmWrite : undefined}
          onCancel={message.pendingConfirmation ? onCancelWrite : undefined}
        />
      ))}
      {isSending && (
        <UDText type="caption-1" color={theme.colors.Gray}>
          <Trans>ИИ-ассистент печатает…</Trans>
        </UDText>
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default React.memo(ChatTranscript);
